import React, {useMemo} from 'react';

import {ProcessFlowchartPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  NodeDetailsSidepanelContext,
  NodeDetailsSidepanelContextValue,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/NodeDetailsSidepanelContext';
import FlowchartContextPanel, {
  PanelSide,
  SidepanelSize,
} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import {NodeDetailsSidepanelContentProps, SidepanelTab} from './processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractiveEndNode,
  isInteractivePlaceholderNode,
  isInteractiveRouterNode,
  isInteractiveTaskNode,
} from './processFlowchartConfigEditorNodeTypes';
import {PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE} from './processFlowchartConfigEditorRegistry';
import EndNodeDetailsSidepanelContent from './right-sidepanel/EndNodeDetailsSidepanelContent';
import PlaceholderNodeDetailsSidepanelContent from './right-sidepanel/PlaceholderNodeDetailsSidepanelContent';
import RouterNodeDetailsSidepanelContent from './right-sidepanel/RouterNodeDetailsSidepanelContent';
import TaskNodeDetailsSidepanelContent from './right-sidepanel/TaskNodeDetailsSidepanelContent';

interface RightSidepanelProps
  extends NodeDetailsSidepanelContentProps,
    Omit<NodeDetailsSidepanelContextValue, 'selectedNode'> {
  selectedNode: FlowchartNode | null;
  selectedFieldId: string | null;
  isOpen: boolean;
  platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>;
  onClose: () => void;
}

function getSidepanelTabsForPlatformFeatures(
  platformFlowchartFeatures?: Map<ProcessFlowchartPlatformFeatureType, boolean>
): Array<SidepanelTab> {
  if (platformFlowchartFeatures == null) {
    return [];
  }
  const platformFeatureSidepanelTabs = [];
  for (const [feature, enabled] of platformFlowchartFeatures) {
    if (enabled && PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE[feature] != null) {
      platformFeatureSidepanelTabs.push(PROCESS_FLOWCHART_SIDEPANEL_TAB_BY_PLATFORM_FEATURE[feature]);
    }
  }
  return platformFeatureSidepanelTabs;
}

function RightSidepanelContent(
  props: RightSidepanelProps & {selectedNode: FlowchartNode}
): JSX.Element | null {
  if (isInteractiveTaskNode(props.selectedNode)) {
    return (
      <TaskNodeDetailsSidepanelContent
        nodeDetailsSidepanelContentProps={{
          selectedNodeTaskDefinitionVersionSpec: props.selectedNode.data.taskDefinitionVersionSpec,
          flowchart: props.flowchart,
          selectedFieldId: props.selectedFieldId,
          locked: props.locked,
          onUpdateTaskDefinitionVersion: props.onUpdateTaskDefinitionVersion,
          onChangeSelectedFieldId: props.onChangeSelectedFieldId,
        }}
        tabs={[
          ...props.selectedNode.data.nodeDetailsSidepanelContent,
          ...getSidepanelTabsForPlatformFeatures(props.platformFlowchartFeatures),
        ]}
      />
    );
  } else if (isInteractivePlaceholderNode(props.selectedNode)) {
    return <PlaceholderNodeDetailsSidepanelContent selectedNode={props.selectedNode} />;
  } else if (isInteractiveRouterNode(props.selectedNode)) {
    return <RouterNodeDetailsSidepanelContent selectedNode={props.selectedNode} />;
  } else if (isInteractiveEndNode(props.selectedNode)) {
    return <EndNodeDetailsSidepanelContent />;
  }
  return null;
}

// TODO(BNCH-126721): Consolidate with transition modal right sidepanel
export default function ProcessFlowchartConfigEditorRightSidepanel(
  props: RightSidepanelProps
): JSX.Element | null {
  const {selectedNode, selectedFieldId, isOpen, onClose, ...contextProps} = props;

  const contextValue = useMemo((): NodeDetailsSidepanelContextValue | null => {
    if (!selectedNode) {
      return null;
    }

    return {
      ...contextProps,
      selectedNode,
    };
  }, [contextProps, selectedNode]);

  return (
    <FlowchartContextPanel
      panelSide={PanelSide.RIGHT}
      sidepanelName={`Node ${selectedNode?.data.nodeName} Details`}
      sidepanelIcon="settings"
      isOpen={isOpen && selectedNode != null}
      onClose={onClose}
      size={SidepanelSize.MEDIUM}
    >
      {contextValue && selectedNode && (
        <NodeDetailsSidepanelContext.Provider key={selectedNode.id} value={contextValue}>
          <div>
            <RightSidepanelContent {...props} selectedNode={selectedNode} />
          </div>
        </NodeDetailsSidepanelContext.Provider>
      )}
    </FlowchartContextPanel>
  );
}
